/**
 * Grand Quack Auto — cosmetic hats for the duck.
 *
 * Visual progression feedback: each chaos tier / special state earns a hat
 * built from primitives. Traffic cone → party hat → crown → chef → viking.
 */

import * as THREE from 'three';
import { DUCK } from '../config';

export type HatType = 'cone' | 'party' | 'crown' | 'chef' | 'viking';

const mat = (color: number) => new THREE.MeshToonMaterial({ color });

const HAT_GROUP_NAME = '__hat__';

/** Height above the duck group origin where the hat sits (top of head). */
function hatY(): number {
  return DUCK.bodyRadius * 1.5 + DUCK.headRadius * 0.9;
}

// ── Hat builders ─────────────────────────────────────────────────────────

function buildTrafficCone(): THREE.Group {
  const g = new THREE.Group();
  const cone = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.35, 8), mat(0xff6600));
  cone.position.y = 0.175;
  g.add(cone);
  // White stripe ring.
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.1, 0.015, 6, 12),
    mat(0xffffff),
  );
  ring.position.y = 0.22;
  ring.rotation.x = Math.PI / 2;
  g.add(ring);
  return g;
}

function buildPartyHat(): THREE.Group {
  const g = new THREE.Group();
  // Colorful striped cone.
  const cone = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.3, 8), mat(0xff44aa));
  cone.position.y = 0.15;
  g.add(cone);
  // Tiny pom-pom on top.
  const pom = new THREE.Mesh(new THREE.SphereGeometry(0.04, 6, 6), mat(0xffff00));
  pom.position.y = 0.32;
  g.add(pom);
  // Elastic band (thin torus around base).
  const band = new THREE.Mesh(
    new THREE.TorusGeometry(0.11, 0.008, 4, 12),
    mat(0x44aaff),
  );
  band.rotation.x = Math.PI / 2;
  band.position.y = 0.01;
  g.add(band);
  return g;
}

function buildCrown(): THREE.Group {
  const g = new THREE.Group();
  // Base band.
  const band = new THREE.Mesh(
    new THREE.CylinderGeometry(0.14, 0.15, 0.1, 8),
    mat(0xffd700),
  );
  band.position.y = 0.05;
  g.add(band);
  // 5 triangle points around the crown.
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const point = new THREE.Mesh(
      new THREE.ConeGeometry(0.04, 0.12, 4),
      mat(0xffd700),
    );
    point.position.set(
      Math.cos(angle) * 0.12,
      0.16,
      Math.sin(angle) * 0.12,
    );
    g.add(point);
  }
  // Red jewel in front.
  const jewel = new THREE.Mesh(new THREE.SphereGeometry(0.025, 6, 6), mat(0xff0000));
  jewel.position.set(0, 0.07, 0.14);
  g.add(jewel);
  return g;
}

function buildChefToque(): THREE.Group {
  const g = new THREE.Group();
  // White cylinder base.
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(0.12, 0.13, 0.08, 8),
    mat(0xffffff),
  );
  base.position.y = 0.04;
  g.add(base);
  // Puffed sphere top.
  const puff = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 8), mat(0xfafafa));
  puff.position.y = 0.16;
  puff.scale.set(1, 1.3, 1);
  g.add(puff);
  return g;
}

function buildVikingHelm(): THREE.Group {
  const g = new THREE.Group();
  // Dark helmet dome.
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(0.16, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2),
    mat(0x444444),
  );
  dome.position.y = 0.0;
  g.add(dome);
  // Two curved horns.
  for (const side of [-1, 1]) {
    const horn = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.22, 6), mat(0xccbb88));
    horn.position.set(side * 0.16, 0.08, 0);
    horn.rotation.z = side * -0.6; // tilt outward
    horn.rotation.x = -0.2;       // slight backward curve
    g.add(horn);
  }
  return g;
}

// ── Public API ───────────────────────────────────────────────────────────

const builders: Record<HatType, () => THREE.Group> = {
  cone: buildTrafficCone,
  party: buildPartyHat,
  crown: buildCrown,
  chef: buildChefToque,
  viking: buildVikingHelm,
};

export function createHat(type: HatType): THREE.Group {
  const hat = builders[type]();
  hat.name = HAT_GROUP_NAME;
  return hat;
}

export function removeHat(duck: THREE.Group): void {
  const existing = duck.getObjectByName(HAT_GROUP_NAME);
  if (existing) duck.remove(existing);
}

export function attachHat(duck: THREE.Group, type: HatType): void {
  removeHat(duck);
  const hat = createHat(type);
  hat.position.y = hatY();
  duck.add(hat);
}

export function getCurrentHatType(duck: THREE.Group): HatType | null {
  const hat = duck.getObjectByName(HAT_GROUP_NAME);
  if (!hat) return null;
  // We stash the type in userData for easy retrieval.
  return (hat.userData.hatType as HatType) ?? null;
}

export function attachHatWithTracking(duck: THREE.Group, type: HatType): void {
  removeHat(duck);
  const hat = createHat(type);
  hat.position.y = hatY();
  hat.userData.hatType = type;
  duck.add(hat);
}
